import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";
import { formatInr, formatPayMonthLabel } from "./runPayrollUtils";
import { exportUnpaidSalaryExcel } from "./exportPaymentLedgerExcel";
import {
  clampYm,
  resolveCompanyMinMonthValue,
} from "../../../utils/selectableDateBounds";

function payMonthNow() {
  return new Date().toISOString().slice(0, 7);
}

export default function UnpaidSalaryReportPanel({ darkMode = false }) {
  const companyMinMonth = resolveCompanyMinMonthValue() || undefined;
  const [fromMonth, setFromMonth] = useState(companyMinMonth || "");
  const [toMonth, setToMonth] = useState(() =>
    clampYm(payMonthNow(), companyMinMonth, null)
  );
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);

  const border = "border-[hsl(var(--ds-border))]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";
  const card = darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-surface))]";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollOpsService.getUnpaidSalaryReport({
        fromMonth: fromMonth || undefined,
        toMonth: toMonth || undefined,
      });
      setData(res?.data || null);
    } catch (err) {
      setData(null);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load unpaid salary");
    } finally {
      setLoading(false);
    }
  }, [fromMonth, toMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const onExport = async () => {
    if (!data?.rows?.length) {
      toast.error("No open unpaid rows to export");
      return;
    }
    setExporting(true);
    try {
      await exportUnpaidSalaryExcel({
        rows: data.rows,
        monthSummary: data.monthSummary || [],
        accumulation: data.accumulation || [],
        fromMonth,
        toMonth,
      });
      toast.success("Unpaid Salary report exported");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totals = data?.totals || {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const monthSummary = Array.isArray(data?.monthSummary) ? data.monthSummary : [];
  const accumulation = Array.isArray(data?.accumulation) ? data.accumulation : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-1">
      <div className={`rounded-xl border p-4 ${border} ${card}`}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Unpaid Salary</h3>
            <p className={`mt-1 text-xs ${muted}`}>
              Open salary and F&amp;F obligations with month-wise and cumulative unpaid
              accumulation. Recovery (negative nets) shown separately.
            </p>
          </div>
          <button
            type="button"
            disabled={exporting || loading || !rows.length}
            onClick={onExport}
            className="inline-flex items-center gap-1.5 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export Excel
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className={muted}>From month</span>
            <input
              type="month"
              value={fromMonth}
              min={companyMinMonth}
              onChange={(e) =>
                setFromMonth(clampYm(e.target.value, companyMinMonth, toMonth || null))
              }
              className={`mt-1 block rounded border px-2 py-1.5 text-xs ${border} bg-[hsl(var(--ds-surface))]`}
            />
          </label>
          <label className="text-xs">
            <span className={muted}>To month</span>
            <input
              type="month"
              value={toMonth}
              min={companyMinMonth}
              onChange={(e) =>
                setToMonth(
                  clampYm(
                    e.target.value,
                    fromMonth || companyMinMonth,
                    null
                  )
                )
              }
              className={`mt-1 block rounded border px-2 py-1.5 text-xs ${border} bg-[hsl(var(--ds-surface))]`}
            />
          </label>
          <button
            type="button"
            disabled={loading}
            onClick={load}
            className={`rounded border px-3 py-1.5 text-xs font-semibold disabled:opacity-45 ${border}`}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Rows", totals.rowCount ?? rows.length],
          ["Salary unpaid", formatInr(totals.salaryUnpaid)],
          ["F&F unpaid", formatInr(totals.fnfUnpaid)],
          ["Total unpaid", formatInr(totals.totalUnpaid)],
          ["Recovery due", formatInr(totals.totalRecovery)],
        ].map(([label, value]) => (
          <div key={label} className={`rounded-lg border px-3 py-2 ${border} ${card}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${muted}`}>{label}</p>
            <p className="text-sm font-semibold tabular-nums">{value}</p>
          </div>
        ))}
      </div>

      <div className={`min-h-0 flex-1 overflow-auto rounded-xl border text-xs ${border}`}>
        <table className="w-full min-w-[1100px]">
          <thead className={`sticky top-0 z-[1] ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
            <tr>
              {[
                "Sl.",
                "Month",
                "Code",
                "Employee",
                "Salary unpaid",
                "F&F unpaid",
                "Hold",
                "Recovery",
                "Total unpaid",
              ].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-bold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.employeeId}-${r.obligationPayMonth}`} className={`border-t ${border}`}>
                <td className="px-2 py-1.5">{i + 1}</td>
                <td className="px-2 py-1.5">{formatPayMonthLabel(r.obligationPayMonth)}</td>
                <td className="px-2 py-1.5">{r.empCode}</td>
                <td className="px-2 py-1.5">{r.empName}</td>
                <td className="px-2 py-1.5 tabular-nums">{formatInr(r.salaryUnpaid)}</td>
                <td className="px-2 py-1.5 tabular-nums">{formatInr(r.fnfUnpaid)}</td>
                <td className="px-2 py-1.5 tabular-nums">{formatInr(r.totalHold)}</td>
                <td className="px-2 py-1.5 tabular-nums">{formatInr(r.totalRecovery)}</td>
                <td className="px-2 py-1.5 font-semibold tabular-nums">{formatInr(r.totalUnpaid)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !rows.length && (
          <p className={`p-8 text-center text-sm ${muted}`}>No open unpaid salary / F&amp;F.</p>
        )}
        {loading && (
          <p className={`flex items-center justify-center gap-2 p-8 text-sm ${muted}`}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </p>
        )}
      </div>

      {!!monthSummary.length && (
        <div className={`rounded-xl border p-3 ${border} ${card}`}>
          <h4 className="mb-2 text-sm font-semibold">Month summary</h4>
          <div className="max-h-40 overflow-auto text-xs">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left">Month</th>
                  <th className="px-2 py-1 text-right">Employees</th>
                  <th className="px-2 py-1 text-right">Unpaid</th>
                  <th className="px-2 py-1 text-right">Hold</th>
                  <th className="px-2 py-1 text-right">Recovery</th>
                </tr>
              </thead>
              <tbody>
                {monthSummary.map((m) => (
                  <tr key={m.obligationPayMonth} className={`border-t ${border}`}>
                    <td className="px-2 py-1">{formatPayMonthLabel(m.obligationPayMonth)}</td>
                    <td className="px-2 py-1 text-right">{m.employees}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatInr(m.totalUnpaid)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatInr(m.totalHold)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatInr(m.totalRecovery)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!accumulation.length && (
        <div className={`rounded-xl border p-3 ${border} ${card}`}>
          <h4 className="mb-2 text-sm font-semibold">Unpaid accumulation (aging)</h4>
          <div className="max-h-40 overflow-auto text-xs">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left">Month</th>
                  <th className="px-2 py-1 text-right">Month unpaid</th>
                  <th className="px-2 py-1 text-right">Cumulative unpaid</th>
                  <th className="px-2 py-1 text-right">Cumulative recovery</th>
                </tr>
              </thead>
              <tbody>
                {accumulation.map((m) => (
                  <tr key={`acc-${m.obligationPayMonth}`} className={`border-t ${border}`}>
                    <td className="px-2 py-1">{formatPayMonthLabel(m.obligationPayMonth)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatInr(m.totalUnpaid)}</td>
                    <td className="px-2 py-1 text-right font-semibold tabular-nums">
                      {formatInr(m.cumulativeUnpaid)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {formatInr(m.cumulativeRecovery)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
